import React, { useState } from 'react'
import Modal from './Modal'
import { Field, inputClass } from './Field'
import { DateRuleType, Frequency, Transfer } from '../types'
import { makeId } from '../lib/id'
import { toISODate } from '../lib/dateRules'
import { DOW_OPTIONS, MONTH_OPTIONS } from './RuleForm'

interface Props {
  initial?: Transfer
  /** Every account in the plan; a transfer needs two of them. */
  accounts: { id: string; name: string }[]
  /** Where a new transfer starts out from — the account in view. */
  defaultFromAccountId?: string
  onSave: (item: Transfer, addAnother: boolean) => void
  onDelete?: () => void
  onClose: () => void
}

export default function TransferForm({ initial, accounts, defaultFromAccountId, onSave, onDelete, onClose }: Props) {
  const firstFrom = initial?.fromAccountId ?? defaultFromAccountId ?? accounts[0]?.id ?? ''
  const [name, setName] = useState(initial?.name ?? '')
  const [amount, setAmount] = useState(initial?.amount?.toString() ?? '')
  const [fromAccountId, setFromAccountId] = useState(firstFrom)
  const [toAccountId, setToAccountId] = useState(
    initial?.toAccountId ?? accounts.find((a) => a.id !== firstFrom)?.id ?? '',
  )
  const [frequency, setFrequency] = useState<Frequency | 'once'>(initial?.frequency ?? 'monthly')
  const [date, setDate] = useState(initial?.date ?? toISODate(new Date()))
  const [dayOfMonth, setDayOfMonth] = useState<string>(
    initial?.dateRule ? (initial.dateRule.dayOfMonth === 'last' ? 'last' : String(initial.dateRule.dayOfMonth)) : '1',
  )
  const [dateRuleType, setDateRuleType] = useState<DateRuleType>(initial?.dateRule?.type ?? 'exact')
  const [monthOfYear, setMonthOfYear] = useState<number>(initial?.monthOfYear ?? new Date().getMonth() + 1)
  const [dayOfWeek, setDayOfWeek] = useState<number>(initial?.dayOfWeek ?? 1)
  const [startDate, setStartDate] = useState(initial?.startDate ?? '')
  const [endDate, setEndDate] = useState(initial?.endDate ?? '')
  const [notes, setNotes] = useState(initial?.notes ?? '')
  const [active, setActive] = useState(initial?.active ?? true)

  const once = frequency === 'once'
  const showYearly = frequency === 'yearly'
  const showMonthly = frequency === 'monthly' || showYearly
  const showWeekly = frequency === 'weekly' || frequency === 'four_weekly'
  const sameAccount = fromAccountId === toAccountId

  /** Picking the account money is already going to swaps the two, rather than leaving both the same. */
  function changeFrom(id: string) {
    if (id === toAccountId) setToAccountId(fromAccountId)
    setFromAccountId(id)
  }

  function changeTo(id: string) {
    if (id === fromAccountId) setFromAccountId(toAccountId)
    setToAccountId(id)
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (sameAccount) return
    const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null
    const addAnother = submitter?.dataset.action === 'add-another'

    const item: Transfer = {
      id: initial?.id ?? makeId(),
      kind: 'transfer',
      name: name.trim() || 'Transfer',
      amount: Math.abs(parseFloat(amount) || 0),
      fromAccountId,
      toAccountId,
      frequency,
      date: once ? date : undefined,
      dateRule: showMonthly
        ? { type: dateRuleType, dayOfMonth: dayOfMonth === 'last' ? 'last' : Number(dayOfMonth) }
        : undefined,
      monthOfYear: showYearly ? monthOfYear : undefined,
      dayOfWeek: showWeekly ? dayOfWeek : undefined,
      startDate: !once && startDate ? startDate : undefined,
      endDate: !once && endDate ? endDate : undefined,
      active,
      notes: notes || undefined,
    }
    onSave(item, addAnother)
  }

  return (
    <Modal title={`${initial ? 'Edit' : 'Add'} transfer`} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <Field label="Name">
          <input
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            placeholder="e.g. Standing order, Holiday pot"
          />
        </Field>

        <Field label="Amount">
          <input className={inputClass} type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} required />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="From">
            <select className={inputClass} value={fromAccountId} onChange={(e) => changeFrom(e.target.value)}>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="To">
            <select className={inputClass} value={toAccountId} onChange={(e) => changeTo(e.target.value)}>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        {sameAccount && <p className="text-xs text-rag-bad -mt-2 mb-3">Pick two different accounts.</p>}

        <Field label="Frequency">
          <select className={inputClass} value={frequency} onChange={(e) => setFrequency(e.target.value as Frequency | 'once')}>
            <option value="once">Once</option>
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
            <option value="four_weekly">Every 4 weeks</option>
            <option value="yearly">Yearly</option>
          </select>
        </Field>

        {once && (
          <Field label="Date">
            <input className={inputClass} type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          </Field>
        )}

        {showYearly && (
          <Field label="Month">
            <select className={inputClass} value={monthOfYear} onChange={(e) => setMonthOfYear(Number(e.target.value))}>
              {MONTH_OPTIONS.map((l, i) => (
                <option key={l} value={i + 1}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
        )}

        {showMonthly && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Day of month">
              <select className={inputClass} value={dayOfMonth} onChange={(e) => setDayOfMonth(e.target.value)}>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
                <option value="last">Last day</option>
              </select>
            </Field>
            <Field label="Rule">
              <select className={inputClass} value={dateRuleType} onChange={(e) => setDateRuleType(e.target.value as DateRuleType)}>
                <option value="exact">Exact date</option>
                <option value="working_day_before">Working day before</option>
                <option value="working_day_after">Working day after</option>
              </select>
            </Field>
          </div>
        )}

        {showWeekly && (
          <Field label="Day of week">
            <select className={inputClass} value={dayOfWeek} onChange={(e) => setDayOfWeek(Number(e.target.value))}>
              {DOW_OPTIONS.map((d) => (
                <option key={d.v} value={d.v}>
                  {d.l}
                </option>
              ))}
            </select>
          </Field>
        )}

        {!once && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Starts (optional)">
              <input className={inputClass} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Field>
            <Field label="Ends (optional)">
              <input className={inputClass} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </Field>
          </div>
        )}

        <Field label="Notes (optional)">
          <input className={inputClass} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>

        <label className="flex items-center gap-2 text-sm text-ink mb-5">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active
        </label>

        <div className="flex items-center justify-between pt-2 border-t border-line">
          <div>
            {onDelete && (
              <button type="button" onClick={onDelete} className="text-sm text-rag-bad hover:underline">
                Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-sm border border-line text-ink/80 hover:bg-paper">
              Cancel
            </button>
            {!initial && (
              <button
                type="submit"
                data-action="add-another"
                disabled={sameAccount}
                className="px-3 py-1.5 text-sm rounded-sm border border-accent text-accent hover:bg-accent/10 disabled:opacity-50"
              >
                Save &amp; add another
              </button>
            )}
            <button
              type="submit"
              data-action="save"
              disabled={sameAccount}
              className="px-3 py-1.5 text-sm rounded-sm bg-accent text-white hover:bg-accent/90 disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
